import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import { ACTION_TYPES, usePurchaseCreateEditDispatch, usePurchaseCreateEditState } from './purchase-create-edit-context';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { IPurchaseAddEditRequest } from '../../../../model/scp/purchase/request/purchase-add-edit-request';
import { useBaseData } from '../../../../application/context/base-data-context';
import { sanitizeText } from '../../../../utils/text';

export const usePurchaseCreateEditController = () => {
    const navigate = useNavigate();
    const { showLoading, showMessage, showDialog } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = usePurchaseCreateEditDispatch();
    const state = usePurchaseCreateEditState();
    const baseData = useBaseData();

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (baseData.loaded) {
            dispatch({ type: ACTION_TYPES.setInstrumentSummons, payload: baseData.instrumentSummons });
            dispatch({ type: ACTION_TYPES.setModality, payload: baseData.modality });
            dispatch({ type: ACTION_TYPES.setDisputeMode, payload: baseData.disputeMode });
        }
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            showLoading({ open: true });

            const [legalSupport, typeDocument, unidEntity] = await Promise.all([
                BaseAppService.listLegalSupport(),
                BaseAppService.listTypeDocument(1),
                EntityAppService.listUnidEntity()
            ]);

            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
            dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocument });
            dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntity });

            if (state.purchaseId !== undefined && state.purchaseId !== null) {
                const data = await PurchaseAppService.getPurchase(state.purchaseId);

                // Recarregar Amparo Legal ANTES de setar o purchaseRequest
                // para garantir que a lista contenha o valor correto
                if (data.typeCallingInstrumentId || data.modalityId) {
                    const filteredLegalSupport = await BaseAppService.listLegalSupport(
                        data.typeCallingInstrumentId,
                        data.modalityId
                    );
                    dispatch({ type: ACTION_TYPES.setLegalSupport, payload: filteredLegalSupport });
                }

                // Mapear os dados da API para o formato do formulário
                const mappedData: IPurchaseAddEditRequest = {
                    id: data.id,
                    title: data.title,
                    situationId: data.situationId,
                    informationComplementary: data.informationComplementary || '',
                    clientId: data.clientId,
                    codBuyerUnit: data.unidadeOrgao?.unitCode?.toString() || '',
                    typeCallingInstrumentId: data.typeCallingInstrumentId,
                    modalityId: data.modalityId,
                    disputeModeId: data.disputeModeId,
                    numberPurchase: data.numberPurchase || '',
                    yearPurchase: data.yearPurchase,
                    processNumber: data.processNumber,
                    objectPurchase: data.objectPurchase || '',
                    notes: data.notes,
                    indSrp: data.indSrp,
                    proposalOpeningDate: data.proposalOpeningDate,
                    proposalCloseDate: data.proposalCloseDate,
                    legalSupportId: data.legalSupportId,
                    urlSystemOrigin: data.urlSystemOrigin,
                };

                dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: mappedData });
            }

            showLoading({ open: false });
        } catch (error) {
            showMessage({
                open: true,
                message: 'Não foi possível carregar os dados da compra/licitação',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
            showLoading({ open: false });
        }
    }

    function handleTabChange(event: any, newTabIndex: number, tabGroup?: number) {
        if (tabGroup === undefined || tabGroup === 1) {
            dispatch({ type: ACTION_TYPES.setTabIndex1, payload: newTabIndex });
        } else if (tabGroup === 2) {
            dispatch({ type: ACTION_TYPES.setTabIndex2, payload: newTabIndex });
        }
    }

    function handleOnChange(e: any) {
        const updates: any = {};
        const fieldMap: Record<string, string> = {
            'select-legal-support': 'legalSupportId',
            'select-type-calling-instrument': 'typeCallingInstrumentId',
            'select-modality': 'modalityId',
            'select-type-document': 'typeDocumentId',
            'select-dispute-mode': 'disputeModeId',
            'select-cod-buyer-unit': 'codBuyerUnit',
            'input-year-purchase': 'yearPurchase',
            'input-number': 'numberPurchase',
            'input-process-number': 'processNumber',
            'input-object': 'objectPurchase',
            'input-cod-procurement-unit': 'procurementUnit',
            'input-title': 'title',
            'input-information-complementary': 'informationComplementary',
            'select-client': 'clientId',
        };

        const textFields = new Set(['input-object', 'input-title', 'input-information-complementary']);

        if (fieldMap[e.target.name]) {
            updates[fieldMap[e.target.name]] = textFields.has(e.target.name)
                ? sanitizeText(e.target.value)
                : e.target.value;
        }

        dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: updates });

        // Recarrega Amparo Legal quando Instrumento Convocatório ou Modalidade mudar
        if (e.target.name === 'select-type-calling-instrument' || e.target.name === 'select-modality') {
            reloadLegalSupport(
                e.target.name === 'select-type-calling-instrument' ? e.target.value : state.purchaseRequest.typeCallingInstrumentId,
                e.target.name === 'select-modality' ? e.target.value : state.purchaseRequest.modalityId
            );
        }
    }

    async function reloadLegalSupport(instrumentSummonsId?: number, modalityId?: number) {
        try {
            const legalSupport = await BaseAppService.listLegalSupport(instrumentSummonsId, modalityId);
            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });

            // Limpa o campo de Amparo Legal selecionado quando os filtros mudarem
            dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: { legalSupportId: 0 } });
        } catch (error) {
            showMessage({
                open: true,
                message: 'Não foi possível recarregar o amparo legal',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
        }
    }

    function handleDateChange(date: any, field: string) {
        const updates: any = {};
        if (date && date.isValid && date.isValid()) {
            updates[field] = date.format('YYYY-MM-DD');
        } else {
            updates[field] = null;
        }
        dispatch({ type: ACTION_TYPES.setPurchaseRequest, payload: updates });
    }

    function setFileUpload(file: File) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function setItemsPurchaseJson(json: string) {
        dispatch({ type: ACTION_TYPES.setItemsPurchaseJson, payload: json });
    }

    function validFields(): boolean {
        const { purchaseRequest } = state;

        if (!state.purchaseId && (!purchaseRequest.title || purchaseRequest.title.length === 0)) return false;
        if (!state.purchaseId && (!purchaseRequest.typeDocumentId || purchaseRequest.typeDocumentId === 0)) return false;
        if (!state.purchaseId && !state.file) return false;
        if (!purchaseRequest.codBuyerUnit || purchaseRequest.codBuyerUnit.length === 0) return false;
        if (!purchaseRequest.typeCallingInstrumentId || purchaseRequest.typeCallingInstrumentId === 0) return false;
        if (!purchaseRequest.modalityId || purchaseRequest.modalityId === 0) return false;
        if (!purchaseRequest.disputeModeId || purchaseRequest.disputeModeId === 0) return false;
        if (!purchaseRequest.legalSupportId || purchaseRequest.legalSupportId === 0) return false;
        if (!purchaseRequest.yearPurchase || purchaseRequest.yearPurchase === 0) return false;
        if (!purchaseRequest.numberPurchase || purchaseRequest.numberPurchase.length === 0) return false;
        if (!purchaseRequest.objectPurchase || purchaseRequest.objectPurchase.length === 0) return false;
        if (!purchaseRequest.clientId || purchaseRequest.clientId === 0) return false;

        return true;
    }

    async function addOrEditPurchase() {
        if (!validFields()) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showMessage({
                open: true,
                message: "Preencha todos os campos obrigatórios",
                type: "error"
            });
            return;
        }

        // Validação: Purchase deve ter pelo menos um item ao criar
        if (state.purchaseId === undefined || state.purchaseId === null) {
            try {
                const items = JSON.parse(state.itemsPurchaseJson || '[]');
                if (!items || items.length === 0) {
                    showMessage({
                        open: true,
                        message: "É necessário adicionar pelo menos um item à compra/licitação",
                        type: "error"
                    });
                    return;
                }
            } catch (error) {
                showMessage({
                    open: true,
                    message: "É necessário adicionar pelo menos um item à compra/licitação",
                    type: "error"
                });
                return;
            }
        }

        setIsSaving(true);
        try {
            showLoading({ open: true });

            if (state.purchaseId === undefined || state.purchaseId === null) {
                await PurchaseAppService.addPurchase(state.purchaseRequest, state.file!, state.itemsPurchaseJson);
                showMessage({ open: true, message: "Compra/Licitação adicionada com sucesso!", type: "success" });
            } else {
                await PurchaseAppService.updatePurchase(state.purchaseId, state.purchaseRequest);
                showMessage({ open: true, message: "Compra/Licitação atualizada com sucesso!", type: "success" });
            }

            showLoading({ open: false });

            showDialog({
                open: true,
                message: "Deseja adicionar uma nova compra/licitação?",
                onYesClick: () => { navigate("/compras-licitacoes/publicar"); },
                onNoClick: () => { navigate("/compras-licitacoes"); }
            } as DefaultDialogProps);
        } catch (error) {
            showMessage({
                open: true,
                message: "Não foi possível salvar a compra/licitação",
                detail: (error as any).details || (error as Error).message,
                type: "error"
            });
            showLoading({ open: false });
        } finally {
            setIsSaving(false);
        }
    }

    return {
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsPurchaseJson,
        validFields,
        addOrEditPurchase,
        isSaving
    };
}
